import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * UID-603 — fires `onIntersect` when a sentinel element scrolls near the
 * viewport, for "load more" infinite scroll. `rootMargin` is generous
 * (below) so the next page is requested BEFORE the sentinel is actually on
 * screen — the explicit "no visible stall" requirement: a shopper scrolling
 * fast never sees a moment with nothing left to render while the next page
 * is still in flight.
 *
 * Returns a REF CALLBACK (not a plain `RefObject`) to attach to a sentinel
 * element (typically an empty div placed after the last rendered row).
 * Does nothing (no observer created) while `enabled` is false, so a caller
 * can gate this off `nextCursor` being non-null — a fully-loaded list
 * mounts no observer at all.
 *
 * Code-review W1 fix: the sentinel's DOM node can unmount and remount while
 * this hook's OWN call site stays mounted — e.g. `PartsLibraryPage`'s
 * Parts<->BOMs tab switch (`Tabs.tsx` only renders the active panel, and the
 * sentinel div lives inside that panel) or `WorkspacePage`'s Parts/BOMs tab.
 * The prior implementation created its `IntersectionObserver` in a
 * mount-time effect keyed on `[enabled]` and read `sentinelRef.current`
 * inside it — if `enabled` never changes across the unmount/remount (it
 * often doesn't: `hasNextPage` can stay `true` the whole time), that effect
 * never re-runs, so the observer keeps watching the now-detached OLD node
 * forever and infinite scroll silently dies until something else (a filter
 * change, a reload) happens to reset the hook's whole call site.
 *
 * The fix tracks the node in `useState` (not a plain ref) precisely so a
 * fresh node — attach OR detach — is a reactive input the effect below
 * re-runs on, in addition to `enabled` changing. A ref callback (rather than
 * `useRef` + an object ref) is what makes that node change observable at
 * all: object refs don't trigger re-renders when `.current` is reassigned.
 */
export interface UseLoadMoreSentinelOptions {
  /**
   * Code-review W4 (UID-603 second pass) — CSS selector for the sentinel's
   * nearest scrolling ancestor, resolved via `node.closest(selector)` when
   * the observer is (re)created. Omitted (default) leaves `root` unset —
   * the browser viewport — which is correct for a page-level scroll
   * container (`PartsLibraryPage`, `WorkspacePage`). Required when the
   * sentinel instead lives inside a scrolling container that ISN'T the
   * viewport (`NewRfqModal`'s `.modal-body`, `overflow-y: auto`):
   * `rootMargin`'s 600px prefetch lead is measured against `root`, so
   * without this the lead is measured against the wrong (viewport) root
   * and the observer only fires once the sentinel reaches the true bottom
   * of the modal — a visible stall instead of a prefetch.
   */
  readonly rootSelector?: string | undefined
  /**
   * Code-review W2 (UID-603 second pass) — an arbitrary value that, when it
   * changes (by `Object.is`), forces the observer to be torn down and
   * recreated against the same node. `IntersectionObserver` only invokes
   * its callback on a THRESHOLD CROSSING (entering/exiting), not on every
   * frame the target continues to intersect — so if the sentinel's
   * surrounding content shrinks (e.g. a client-side search filters the
   * newly-appended page down to zero rows) while the sentinel itself
   * never fully leaves the intersecting region, no new callback fires and
   * `loadMore()` never gets called again, silently stalling pagination.
   * Re-creating the observer forces a fresh `observe()` call, which always
   * reports the CURRENT intersection state immediately — catching that
   * "still intersecting after content changed" case. Pass something that
   * changes whenever the visible list could have changed shape, e.g. the
   * loaded item count.
   */
  readonly recheckKey?: unknown
}

export function useLoadMoreSentinel(
  onIntersect: () => void,
  enabled: boolean,
  options?: UseLoadMoreSentinelOptions,
) {
  const onIntersectRef = useRef(onIntersect)
  onIntersectRef.current = onIntersect

  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const sentinelRef = useCallback((next: HTMLDivElement | null) => {
    setNode(next)
  }, [])

  const rootSelector = options?.rootSelector
  const recheckKey = options?.recheckKey

  // biome-ignore lint/correctness/useExhaustiveDependencies: `recheckKey` is deliberately in the dependency list purely to force a disconnect+re-observe cycle (see its doc comment above) — it is never read inside the effect body.
  useEffect(() => {
    if (!enabled || !node) return
    const root = rootSelector ? node.closest(rootSelector) : null
    // Prefetch ~600px before the sentinel reaches the viewport, rather than
    // exactly at it — the "prefetch before hitting the bottom" requirement.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onIntersectRef.current()
      },
      { root, rootMargin: '0px 0px 600px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
    // `recheckKey` is intentionally an effect dependency purely to force a
    // disconnect+re-observe cycle (see its doc comment above) — it is never
    // read inside the effect body.
  }, [enabled, node, rootSelector, recheckKey])

  return sentinelRef
}
